import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Paper,
    Title,
    Text,
    Group,
    Button,
    Alert,
    Modal,
    Tooltip,
    Table,
    TextInput,
    Textarea,
    Stack,
    NumberInput,
    ActionIcon,
    Center,
    Divider,
    List,
    Loader,
    Badge
} from '@mantine/core';
import {
    IconDatabase,
    IconRefresh,
    IconPlus,
    IconCheck,
    IconDownload,
    IconEdit,
    IconTrash,
    IconInfoCircle,
    IconAlertCircle
} from '@tabler/icons-react';
import config from 'config/config';
import { buildDemoDownloadUrl } from 'services/demoMatchesService';
import demoMatchesService from 'services/demoMatchesService';
import { DemoMatch } from 'types/demos';
import { getDisplayName } from 'utils/demoUtils';
import { calculateDemoInsights } from 'utils/demoInsights';
import { formatDate, formatDateForInput, parseInputDateToISO } from './intraUtils';

/**
 * Props for the DemoArchiveSection component.
 */
interface DemoArchiveSectionProps {
    /** JWT access token for authenticated API requests, null if not logged in */
    accessToken: string | null;
}

/**
 * CS2 demo archive management section for the Intra admin dashboard.
 *
 * Provides full CRUD functionality for managing CS2 match demo files:
 * - View all demo matches in a detailed table
 * - Download demo files directly
 * - Create new demo match entries with full metadata
 * - Edit existing match details (tournament, teams, scores, players)
 * - Delete demo matches with confirmation
 * - Copy filenames to clipboard
 *
 * Also displays archive insights including:
 * - Total match count
 * - Average match duration
 * - Most played map statistics
 * - Winning streaks
 * - Recent winners list
 *
 * @param props - The component props
 * @returns A complete demo archive management interface
 */
const DemoArchiveSection: React.FC<DemoArchiveSectionProps> = ({ accessToken }) => {
    const [matches, setMatches] = useState<DemoMatch[]>([]);
    const [demosLoading, setDemosLoading] = useState(false);
    const [demosError, setDemosError] = useState<string | undefined>();
    const [refreshIndex, setRefreshIndex] = useState(0);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState<DemoMatch | null>(null);
    const [formData, setFormData] = useState<Partial<DemoMatch>>({});
    const [crudLoading, setCrudLoading] = useState(false);
    const [crudError, setCrudError] = useState<string | null>(null);
    const [copiedMatchId, setCopiedMatchId] = useState<string | null>(null);

    const insights = useMemo(() => calculateDemoInsights(matches), [matches]);

    const loadMatches = useCallback(async () => {
        setDemosLoading(true);
        setDemosError(undefined);
        try {
            let loadedMatches: DemoMatch[];
            if (accessToken) {
                loadedMatches = await demoMatchesService.fetchDemoMatchesAuthenticated(accessToken);
            } else {
                loadedMatches = await demoMatchesService.fetchDemoMatches();
            }
            setMatches(loadedMatches);
        } catch (err) {
            setDemosError(err instanceof Error ? err.message : 'Tuntematon virhe');
        } finally {
            setDemosLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        loadMatches();
    }, [loadMatches, refreshIndex]);

    const refresh = useCallback(() => {
        setRefreshIndex((value) => value + 1);
    }, []);

    const handleCreateClick = () => {
        setFormData({
            id: '',
            tournamentId: 'cs2',
            event: '',
            stage: '',
            displayName: '',
            fileSizeMB: 0,
            map: '',
            bestOf: 1,
            playedAt: new Date().toISOString(),
            teams: [
                { name: '', score: 0, players: [] },
                { name: '', score: 0, players: [] }
            ]
        });
        setSelectedMatch(null);
        setCrudError(null);
        setEditModalOpen(true);
    };

    const handleEditClick = (match: DemoMatch) => {
        setFormData({ ...match });
        setSelectedMatch(match);
        setCrudError(null);
        setEditModalOpen(true);
    };

    const handleDeleteClick = (match: DemoMatch) => {
        setSelectedMatch(match);
        setCrudError(null);
        setDeleteModalOpen(true);
    };

    const validateAuth = (): boolean => {
        if (!accessToken) {
            setCrudError('Autentikointi puuttuu. Kirjaudu sisään uudelleen.');
            return false;
        }
        return true;
    };

    const handleFormSubmit = async () => {
        if (!validateAuth()) return;

        if (!formData.id || !formData.tournamentId || !formData.event || !formData.stage) {
            setCrudError('Täytä kaikki pakolliset kentät');
            return;
        }

        setCrudLoading(true);
        setCrudError(null);
        try {
            if (selectedMatch) {
                await demoMatchesService.updateDemoMatch(selectedMatch.id, formData, accessToken!);
            } else {
                await demoMatchesService.createDemoMatch(formData as DemoMatch, accessToken!);
            }
            setEditModalOpen(false);
            refresh();
        } catch (err) {
            setCrudError(err instanceof Error ? err.message : 'Tuntematon virhe');
        } finally {
            setCrudLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedMatch) return;
        if (!validateAuth()) return;

        setCrudLoading(true);
        setCrudError(null);
        try {
            await demoMatchesService.deleteDemoMatch(selectedMatch.id, accessToken!);
            setDeleteModalOpen(false);
            setSelectedMatch(null);
            refresh();
        } catch (err) {
            setCrudError(err instanceof Error ? err.message : 'Tuntematon virhe');
        } finally {
            setCrudLoading(false);
        }
    };

    const handleFormFieldChange = (field: keyof DemoMatch, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleTeamFieldChange = (teamIndex: number, field: string, value: any) => {
        setFormData(prev => {
            const teams = [...(prev.teams || [])];
            if (teams[teamIndex]) {
                teams[teamIndex] = { ...teams[teamIndex], [field]: value };
            }
            return { ...prev, teams: teams as [any, any] };
        });
    };

    const handlePlayerChange = (teamIndex: number, players: string[]) => {
        handleTeamFieldChange(teamIndex, 'players', players);
    };

    const handleCopyFileName = async (matchId: string, fileName: string, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        try {
            await navigator.clipboard.writeText(fileName);
            setCopiedMatchId(matchId);
            setTimeout(() => {
                setCopiedMatchId(null);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            const textArea = document.createElement('textarea');
            textArea.value = fileName;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedMatchId(matchId);
                setTimeout(() => {
                    setCopiedMatchId(null);
                }, 2000);
            } catch (fallbackErr) {
                console.error('Fallback copy failed:', fallbackErr);
            } finally {
                document.body.removeChild(textArea);
            }
        }
    };

    return (
        <>
            {demosError && (
                <Alert
                    color="red"
                    icon={<IconAlertCircle size={16} />}
                    title="Demo-metadatan haku epäonnistui"
                    withCloseButton
                    onClose={refresh}
                    mb="md"
                >
                    {demosError}
                </Alert>
            )}
            <Paper shadow="sm" p="md" radius="md" withBorder>
                <Title order={3} mb="xs">
                    <Group gap="xs">
                        <IconDatabase size={20} />
                        <span>CS2-demoarkisto</span>
                    </Group>
                </Title>
                <Text size="sm" c="dimmed" mb="md">
                    Tiedostot luetaan polusta <code>{config.DEMO_FILES_BASE_PATH}</code> ja julkaistaan
                    suoraan frontista.
                </Text>
                <Group gap="xs" mb="md">
                    <Button
                        leftSection={<IconRefresh size={16} />}
                        onClick={refresh}
                        loading={demosLoading}
                        disabled={demosLoading}
                    >
                        Päivitä demot
                    </Button>
                    <Button
                        color="blue"
                        leftSection={<IconPlus size={16} />}
                        onClick={handleCreateClick}
                    >
                        Lisää demo
                    </Button>
                </Group>
                {demosLoading ? (
                    <Center style={{ marginTop: '1rem' }}>
                        <Loader size="sm">Ladataan demoja...</Loader>
                    </Center>
                ) : matches.length === 0 ? (
                    <Alert icon={<IconInfoCircle size={16} />} color="blue" style={{ marginTop: '1rem' }}>
                        Demoarkisto on tyhjä – siirrä uusia <code>.dem</code>-tiedostoja kansioon ja lisää metadata
                        mock-API:in.
                    </Alert>
                ) : (
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginTop: '1rem' }}>
                        <Table striped highlightOnHover withTableBorder withColumnBorders style={{ fontSize: '0.875rem' }}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Tiedosto</Table.Th>
                                    <Table.Th>Turnaus & vaihe</Table.Th>
                                    <Table.Th>Kartta</Table.Th>
                                    <Table.Th>Pvm</Table.Th>
                                    <Table.Th>Joukkueet</Table.Th>
                                    <Table.Th style={{ textAlign: 'right' }}>Koko</Table.Th>
                                    <Table.Th>Toiminnot</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {matches.map(match => {
                                    const [teamA, teamB] = match.teams;
                                    return (
                                        <Table.Tr key={match.id}>
                                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                                                <div>
                                                    {getDisplayName(match)}
                                                    {match.fileName && (
                                                        <Tooltip label={copiedMatchId === match.id ? 'Kopioitu leikepöydälle!' : `Tiedosto: ${match.fileName}`}>
                                                            <ActionIcon
                                                                variant="subtle"
                                                                size="sm"
                                                                style={{
                                                                    marginLeft: '4px',
                                                                    color: copiedMatchId === match.id ? '#21ba45' : '#999',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={(e: React.MouseEvent) => handleCopyFileName(match.id, match.fileName!, e)}
                                                            >
                                                                {copiedMatchId === match.id ? <IconCheck size={16} /> : <IconInfoCircle size={16} />}
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                <Button
                                                    component="a"
                                                    href={buildDemoDownloadUrl(match.id)}
                                                    size="xs"
                                                    leftSection={<IconDownload size={14} />}
                                                    download={getDisplayName(match)}
                                                    style={{ marginTop: '0.5rem' }}
                                                >
                                                    Lataa
                                                </Button>
                                            </Table.Td>
                                            <Table.Td>
                                                <strong>{match.event}</strong>
                                                <div>
                                                    <Badge size="sm" color="orange">
                                                        {match.stage}
                                                    </Badge>
                                                </div>
                                            </Table.Td>
                                            <Table.Td>{match.map}</Table.Td>
                                            <Table.Td>{formatDate(match.playedAt)}</Table.Td>
                                            <Table.Td>
                                                <List size="sm" withPadding>
                                                    <List.Item>
                                                        {teamA.name} ({teamA.score}) – {teamA.players.join(', ')}
                                                    </List.Item>
                                                    <List.Item>
                                                        {teamB.name} ({teamB.score}) – {teamB.players.join(', ')}
                                                    </List.Item>
                                                </List>
                                            </Table.Td>
                                            <Table.Td style={{ textAlign: 'right' }}>
                                                {match.fileSizeMB.toFixed(1)} MB
                                            </Table.Td>
                                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                                                <Group gap="xs">
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        onClick={() => handleEditClick(match)}
                                                    >
                                                        <IconEdit size={16} />
                                                    </ActionIcon>
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        color="red"
                                                        onClick={() => handleDeleteClick(match)}
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                    </div>
                )}
                <Paper shadow="sm" p="md" radius="md" withBorder mt="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                    <Title order={4} mb="xs">
                        <Group gap="xs">
                            <IconInfoCircle size={18} />
                            <span>Tilannekuva</span>
                        </Group>
                    </Title>
                    <List size="sm" spacing="xs">
                        <List.Item>Arkistossa {insights.totalMatches} tallennetta.</List.Item>
                        {typeof insights.avgDurationMinutes === 'number' && (
                            <List.Item>Keskimääräinen kesto {insights.avgDurationMinutes} minuuttia.</List.Item>
                        )}
                        {insights.mostPlayedMap && (
                            <List.Item>
                                Useimmin pelattu kartta {insights.mostPlayedMap.map} (
                                {insights.mostPlayedMap.count} kpl).
                            </List.Item>
                        )}
                        {insights.hottestStreak && (
                            <List.Item>
                                Kuumin voittoputki: {insights.hottestStreak.team} (
                                {insights.hottestStreak.wins} peräkkäistä voittoa).
                            </List.Item>
                        )}
                        {insights.recentWinners.length > 0 && (
                            <List.Item>
                                Viimeisimmät voittajat:
                                <List size="sm" withPadding mt="xs">
                                    {insights.recentWinners.map(item => (
                                        <List.Item key={item.matchId}>
                                            {item.team} – {formatDate(item.playedAt)}
                                        </List.Item>
                                    ))}
                                </List>
                            </List.Item>
                        )}
                    </List>
                    <Alert icon={<IconInfoCircle size={16} />} color="blue" mt="md">
                        <Title order={5} mb="xs">Ohje</Title>
                        <Text size="sm">
                            Lataa uudet <code>.dem</code>-tiedostot kansioon <code>demo-files/</code> ja lisää
                            metatiedot joko mock-jsoniin (<code>/public/mock/demo-matches.json</code>) tai tulevaan
                            API:in. Päivitykset heijastuvat tähän näkymään ja julkiselle turnaussivulle.
                        </Text>
                    </Alert>
                </Paper>
            </Paper>

            {/* Create/Edit Modal */}
            <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} size="lg" title={selectedMatch ? 'Muokkaa demo-ottelua' : 'Lisää uusi demo-ottelu'}>
                <Stack gap="md">
                    {crudError && (
                        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Virhe">
                            {crudError}
                        </Alert>
                    )}
                    <form>
                        <Group grow>
                            <TextInput
                                label="ID"
                                value={formData.id || ''}
                                onChange={(e) => handleFormFieldChange('id', e.target.value)}
                                disabled={!!selectedMatch}
                                required
                            />
                            <TextInput
                                label="Turnaus ID"
                                value={formData.tournamentId || ''}
                                onChange={(e) => handleFormFieldChange('tournamentId', e.target.value)}
                                required
                            />
                        </Group>
                        <Group grow>
                            <TextInput
                                label="Tapahtuma"
                                value={formData.event || ''}
                                onChange={(e) => handleFormFieldChange('event', e.target.value)}
                                required
                            />
                            <TextInput
                                label="Vaihe"
                                value={formData.stage || ''}
                                onChange={(e) => handleFormFieldChange('stage', e.target.value)}
                                required
                            />
                        </Group>
                        <TextInput
                            label="Näyttönimi (valinnainen)"
                            value={formData.displayName || ''}
                            onChange={(e) => handleFormFieldChange('displayName', e.target.value)}
                            placeholder="esim. Finaali - Joukkue A vs Joukkue B"
                        />
                        <Group grow>
                            <TextInput
                                label="Kartta"
                                value={formData.map || ''}
                                onChange={(e) => handleFormFieldChange('map', e.target.value)}
                            />
                            <NumberInput
                                label="BestOf"
                                min={1}
                                value={formData.bestOf || 1}
                                onChange={(value) => handleFormFieldChange('bestOf', Number(value))}
                                required
                            />
                        </Group>
                        <Group grow>
                            <NumberInput
                                label="Tiedostokoko (MB)"
                                step={0.1}
                                min={0}
                                value={formData.fileSizeMB || 0}
                                onChange={(value) => handleFormFieldChange('fileSizeMB', Number(value))}
                            />
                            <TextInput
                                label="Peliaika"
                                type="datetime-local"
                                step={1}
                                value={formData.playedAt ? formatDateForInput(formData.playedAt) : ''}
                                onChange={(e) => {
                                    const date = e.target.value ? parseInputDateToISO(e.target.value) : '';
                                    handleFormFieldChange('playedAt', date);
                                }}
                            />
                        </Group>
                        <Group grow>
                            <NumberInput
                                label="Kesto (minuuttia)"
                                min={0}
                                value={formData.durationMinutes || undefined}
                                onChange={(value) => handleFormFieldChange('durationMinutes', value ? Number(value) : undefined)}
                            />
                            <NumberInput
                                label="Kierrokset"
                                min={0}
                                value={formData.rounds || undefined}
                                onChange={(value) => handleFormFieldChange('rounds', value ? Number(value) : undefined)}
                            />
                        </Group>
                        <Divider label="Joukkue 1" labelPosition="left" />
                        <Group grow>
                            <TextInput
                                label="Nimi"
                                value={formData.teams?.[0]?.name || ''}
                                onChange={(e) => handleTeamFieldChange(0, 'name', e.target.value)}
                            />
                            <NumberInput
                                label="Pisteet"
                                min={0}
                                value={formData.teams?.[0]?.score || 0}
                                onChange={(value) => handleTeamFieldChange(0, 'score', Number(value))}
                            />
                        </Group>
                        <TextInput
                            label="Pelaajat (pilkulla erotettu)"
                            value={formData.teams?.[0]?.players?.join(', ') || ''}
                            onChange={(e) => {
                                const players = e.target.value.split(',').map(p => p.trim()).filter(Boolean);
                                handlePlayerChange(0, players);
                            }}
                            placeholder="Pelaaja1, Pelaaja2, Pelaaja3..."
                        />
                        <Divider label="Joukkue 2" labelPosition="left" />
                        <Group grow>
                            <TextInput
                                label="Nimi"
                                value={formData.teams?.[1]?.name || ''}
                                onChange={(e) => handleTeamFieldChange(1, 'name', e.target.value)}
                            />
                            <NumberInput
                                label="Pisteet"
                                min={0}
                                value={formData.teams?.[1]?.score || 0}
                                onChange={(value) => handleTeamFieldChange(1, 'score', Number(value))}
                            />
                        </Group>
                        <TextInput
                            label="Pelaajat (pilkulla erotettu)"
                            value={formData.teams?.[1]?.players?.join(', ') || ''}
                            onChange={(e) => {
                                const players = e.target.value.split(',').map(p => p.trim()).filter(Boolean);
                                handlePlayerChange(1, players);
                            }}
                            placeholder="Pelaaja1, Pelaaja2, Pelaaja3..."
                        />
                        <Textarea
                            label="Muistiinpanot"
                            value={formData.notes || ''}
                            onChange={(e) => handleFormFieldChange('notes', e.target.value)}
                        />
                    </form>
                </Stack>
                <Group justify="flex-end" mt="md">
                    <Button variant="subtle" onClick={() => setEditModalOpen(false)}>Peruuta</Button>
                    <Button color="blue" onClick={handleFormSubmit} loading={crudLoading}>
                        {selectedMatch ? 'Tallenna muutokset' : 'Luo demo-ottelu'}
                    </Button>
                </Group>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} size="sm" title="Poista demo-ottelu">
                <Stack gap="md">
                    {crudError && (
                        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Virhe">
                            {crudError}
                        </Alert>
                    )}
                    <Text>
                        Haluatko varmasti poistaa demo-ottelun <strong>{selectedMatch ? getDisplayName(selectedMatch) : ''}</strong>?
                        Tämä toiminto on peruuttamaton.
                    </Text>
                </Stack>
                <Group justify="flex-end" mt="md">
                    <Button variant="subtle" onClick={() => setDeleteModalOpen(false)}>Peruuta</Button>
                    <Button color="red" onClick={handleDeleteConfirm} loading={crudLoading}>
                        Poista
                    </Button>
                </Group>
            </Modal>
        </>
    );
};

export default DemoArchiveSection;

